/**
 * Web stand-in for `@react-native-documents/picker`.
 *
 * Mirrors the surface that zen-core-elements' `pickFile()` relies on:
 *   DocumentPicker.pick({type, allowMultiSelection})
 *   DocumentPicker.types.<mime>
 *   DocumentPicker.isErrorWithCode(err)
 *
 * Results carry the same `{uri, name, size, type}` shape the native module
 * returns, so downstream code (including base64 conversion through the
 * react-native-blob-util shim) works unchanged.
 */

import {openFileDialog} from './webFileInput';

export const errorCodes = {
  OPERATION_CANCELED: 'OPERATION_CANCELED',
  IN_PROGRESS: 'ASYNC_OP_IN_PROGRESS',
  UNABLE_TO_OPEN_FILE_TYPE: 'UNABLE_TO_OPEN_FILE_TYPE',
} as const;

/** Browser file dialogs take MIME types directly. */
export const types = {
  allFiles: '*/*',
  audio: 'audio/*',
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  images: 'image/*',
  pdf: 'application/pdf',
  plainText: 'text/plain',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  video: 'video/*',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  zip: 'application/zip',
} as const;

export type DocumentPickerResponse = {
  uri: string;
  name: string | null;
  size: number | null;
  type: string | null;
  fileCopyUri: string | null;
  /** Web-only escape hatch for direct uploads. */
  webFile?: File;
};

export class DocumentPickerCanceledError extends Error {
  code = errorCodes.OPERATION_CANCELED;
  constructor() {
    super('User canceled document picker');
    this.name = 'DocumentPickerCanceledError';
  }
}

export type PickOptions = {
  type?: string | string[];
  allowMultiSelection?: boolean;
  [key: string]: any;
};

export async function pick(options: PickOptions = {}): Promise<DocumentPickerResponse[]> {
  const requested = options.type;
  const accept = Array.isArray(requested) ? requested.join(',') : requested || types.allFiles;

  const picked = await openFileDialog({
    accept: accept === types.allFiles ? undefined : accept,
    multiple: Boolean(options.allowMultiSelection),
  });

  if (picked.length === 0) {
    // The native module rejects on cancel; callers catch and check the code.
    throw new DocumentPickerCanceledError();
  }

  return picked.map(file => ({
    uri: file.uri,
    name: file.name,
    size: file.size,
    type: file.type || null,
    fileCopyUri: file.uri,
    webFile: file.file,
  }));
}

export const pickSingle = async (options: PickOptions = {}): Promise<DocumentPickerResponse> => {
  const result = await pick({...options, allowMultiSelection: false});
  return result[0];
};

export function isErrorWithCode(error: unknown): boolean {
  return Boolean(error && typeof (error as any).code === 'string');
}

export function isCancel(error: unknown): boolean {
  return Boolean(error) && (error as any).code === errorCodes.OPERATION_CANCELED;
}

export function isInProgress(error: unknown): boolean {
  return Boolean(error) && (error as any).code === errorCodes.IN_PROGRESS;
}

export async function releaseSecureAccess(): Promise<void> {}

const DocumentPicker = {
  pick,
  pickSingle,
  types,
  errorCodes,
  isErrorWithCode,
  isCancel,
  isInProgress,
  releaseSecureAccess,
};

export default DocumentPicker;
